import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule } from '@ngx-translate/core';

import {
  AnalyticsService,
  DmmViolationNotice,
  DmmViolationSession,
} from '../../analytics/analytics.service';

export interface DmmViolationDialogData {
  notice: DmmViolationNotice;
}

@Component({
  selector: 'app-dmm-violation-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    TranslateModule,
  ],
  template: `
    <h2 mat-dialog-title>
      {{ (isCapacity ? 'diagnostics.dmm_violation.over_capacity_dialog.title' : 'diagnostics.dmm_violation.dialog.title') | translate }}
      @if (data.notice.athlete_name) {
        <span class="dialog-athlete">— {{ data.notice.athlete_name }}</span>
      }
    </h2>

    <mat-dialog-content class="dialog-content">
      <p class="dialog-subtitle">
        @if (isCapacity) {
          {{ 'diagnostics.dmm_violation.over_capacity_dialog.subtitle' | translate:{
               n: sessions.length,
               minutes: capacityMinutes,
               since: (data.notice.window_start | date:'dd.MM.yyyy') } }}
        } @else {
          {{ 'diagnostics.dmm_violation.dialog.subtitle' | translate:{ n: sessions.length } }}
        }
      </p>

      @if (loading) {
        <div class="dialog-loading">
          <mat-spinner diameter="22"></mat-spinner>
          <span>{{ 'diagnostics.dmm_violation.dialog.loading' | translate }}</span>
        </div>
      }

      @if (sessions.length > 0) {
        <table class="violation-table">
          <thead>
            <tr>
              <th>{{ 'diagnostics.dmm_violation.dialog.col_date' | translate }}</th>
              @if (isCapacity) {
                <th>{{ 'diagnostics.dmm_violation.over_capacity_dialog.col_over_capacity' | translate }}</th>
                <th>{{ 'diagnostics.dmm_violation.over_capacity_dialog.col_episodes' | translate }}</th>
              } @else {
                <th>{{ 'diagnostics.dmm_violation.dialog.col_coverage' | translate }}</th>
              }
              <th>{{ 'diagnostics.dmm_violation.dialog.col_severity' | translate }}</th>
              @if (!isCapacity) {
                <th>{{ 'diagnostics.dmm_violation.dialog.col_excess_min' | translate }}</th>
              }
              <th class="col-action"></th>
            </tr>
          </thead>
          <tbody>
            @for (s of sessions; track s.session_id) {
              <tr class="violation-row"
                  [class.row-busy]="rowBusy[s.session_id]">
                <td (click)="openSession(s)">
                  <strong>{{ s.date }}</strong>
                  @if (s.name) { <span class="muted"> — {{ s.name }}</span> }
                </td>
                @if (isCapacity) {
                  <td (click)="openSession(s)">{{ formatMinutes(s.over_capacity_s) }}</td>
                  <td (click)="openSession(s)">{{ s.over_capacity_episodes ?? '—' }}</td>
                } @else {
                  <td (click)="openSession(s)">
                    {{ s.coverage_pct == null ? '—' : (s.coverage_pct | number:'1.0-0') + '%' }}
                  </td>
                }
                <td (click)="openSession(s)">
                  <span class="severity-badge"
                        [class.severity-spike]="s.severity === 'spike'"
                        [class.severity-mixed]="s.severity === 'mixed'"
                        [class.severity-sustained]="s.severity === 'sustained'">
                    {{ ((isCapacity ? 'diagnostics.dmm_violation.over_capacity_dialog.severity.' : 'diagnostics.dmm_violation.dialog.severity.') + s.severity) | translate }}
                  </span>
                </td>
                @if (!isCapacity) {
                  <td (click)="openSession(s)">{{ formatMinutes(s.sec_avg_above_maxlass) }}</td>
                }
                <td class="col-action">
                  <button mat-icon-button
                          type="button"
                          [disabled]="rowBusy[s.session_id] || busy"
                          (click)="ackSession(s); $event.stopPropagation()"
                          [attr.title]="'diagnostics.dmm_violation.dialog.row_dismiss' | translate"
                          aria-label="dismiss this session">
                    <mat-icon>close</mat-icon>
                  </button>
                </td>
              </tr>
            }
          </tbody>
        </table>
      }

      @if (!loading && sessions.length === 0) {
        <p class="empty-state">
          {{ 'diagnostics.dmm_violation.dialog.empty' | translate }}
        </p>
      }
    </mat-dialog-content>

    <mat-dialog-actions class="dialog-footer">
      <span class="grow"></span>
      <button mat-button [disabled]="busy || sessions.length === 0" (click)="dismissAll()">
        {{ 'diagnostics.dmm_violation.dialog.action_dismiss_all' | translate }}
      </button>
      <button mat-raised-button color="primary" [disabled]="busy" (click)="newDiagnostic()">
        {{ 'diagnostics.dmm_violation.dialog.action_new_diag' | translate }}
      </button>
    </mat-dialog-actions>
  `,
  styles: [`
    .dialog-athlete { color: var(--text-secondary, #666); font-weight: 400; margin-left: 0.5rem; }
    .dialog-subtitle { color: var(--text-secondary, #666); margin-top: 0; }
    .dialog-content { min-width: 600px; max-width: 760px; }
    .dialog-loading { display: flex; align-items: center; gap: 0.5rem;
                      padding: 0.5rem 0; color: var(--text-secondary, #666); }
    .empty-state { color: var(--text-secondary, #888); font-style: italic; padding: 0.5rem 0; }
    .violation-table {
      width: 100%; border-collapse: collapse; margin-top: 0.5rem;
      font-size: var(--fs-sm); font-variant-numeric: tabular-nums;
    }
    .violation-table thead th {
      text-align: left; padding: 0.4rem 0.6rem;
      color: var(--text-secondary, #666); font-weight: 500;
      border-bottom: 1px solid var(--mat-sys-outline-variant, #ccd5da);
    }
    .violation-row { transition: opacity 0.2s ease; }
    .violation-row.row-busy { opacity: 0.4; pointer-events: none; }
    .violation-row td:not(.col-action) { cursor: pointer; }
    .violation-row td:not(.col-action):hover { background: var(--surface-hover, #f5f5f5); }
    .violation-row td {
      padding: 0.35rem 0.6rem;
      border-bottom: 1px solid var(--mat-sys-outline-variant, #ececec);
      vertical-align: middle;
    }
    .col-action { width: 2.5rem; text-align: right; padding-right: 0.25rem; }
    .col-action button { width: 32px; height: 32px; line-height: 32px; }
    .col-action mat-icon { font-size: 18px; line-height: 18px; width: 18px; height: 18px; }
    .muted { color: var(--text-secondary, #888); }
    .severity-badge {
      display: inline-block; padding: 0.05rem 0.4rem; border-radius: 999px;
      font-size: 0.7rem; font-weight: 500;
    }
    .severity-spike     { background: #fff3e0; color: #e65100; }
    .severity-mixed     { background: #fff8e1; color: #ef6c00; }
    .severity-sustained { background: #ffebee; color: #c62828; }
    .dialog-footer { display: flex; align-items: center; gap: 0.5rem; }
    .grow { flex: 1; }
  `],
})
export class DmmViolationDialogComponent implements OnInit {
  private readonly analytics = inject(AnalyticsService);
  private readonly router = inject(Router);
  private readonly dialogRef = inject(MatDialogRef<DmmViolationDialogComponent>);
  private readonly cdr = inject(ChangeDetectorRef);

  sessions: DmmViolationSession[] = [];
  loading = false;
  busy = false;
  /** session_id -> wird gerade ack'd (UI-Disable + Fade). */
  rowBusy: Record<number, boolean> = {};
  /** Wurde mind. eine Session ack'd? — wir geben dem Banner bescheid beim Schliessen. */
  private acknowledged = false;

  constructor(@Inject(MAT_DIALOG_DATA) public data: DmmViolationDialogData) {}

  ngOnInit(): void {
    // Wir laden IMMER frisch vom Server — Snapshot der Notice ist evtl.
    // veraltet (er reflektiert keine zwischenzeitlich ack'd Sessions).
    this.loading = true;
    this.analytics.getDmmViolationSessions(this.data.notice.id).subscribe({
      next: (r) => {
        this.sessions = r.sessions ?? [];
        this.loading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.sessions = [];
        this.loading = false;
        this.cdr.markForCheck();
      },
    });
  }

  /** Kapazitaets-Hinweis (Einfrierregel) statt alter Abbruch-Liste. */
  get isCapacity(): boolean { return this.data.notice.kind === 'over_capacity'; }

  /** Minuten ueber Kapazitaet der aktuell offenen Einheiten (nach Abhaken neu). */
  get capacityMinutes(): number {
    return Math.round(this.sessions.reduce((a, s) => a + (s.over_capacity_s ?? 0), 0) / 60);
  }

  formatMinutes(seconds: number | null | undefined): string {
    if (!seconds || seconds <= 0) return '—';
    const m = Math.floor(seconds / 60);
    const s = Math.round(seconds % 60);
    if (m === 0) return `${s}s`;
    return s === 0 ? `${m}m` : `${m}m ${s}s`;
  }

  openSession(s: DmmViolationSession): void {
    this.dialogRef.close({ navigated: true, acknowledged: this.acknowledged });
    this.router.navigate(['/training-analysis', s.session_id]);
  }

  ackSession(s: DmmViolationSession): void {
    if (this.rowBusy[s.session_id] || this.busy) return;
    this.rowBusy[s.session_id] = true;
    this.cdr.markForCheck();
    this.analytics.acknowledgeDmmViolationSession(s.session_id, { action: 'dismiss' })
      .subscribe({
        next: (res) => {
          this.acknowledged = true;
          // Lokale Liste aktualisieren — Zeile fliegt raus
          this.sessions = this.sessions.filter(x => x.session_id !== s.session_id);
          delete this.rowBusy[s.session_id];
          this.cdr.markForCheck();
          // Wenn keine Sessions mehr → Dialog kann zu, Banner verschwindet automatisch
          if (this.sessions.length === 0 || res.notice_action === 'auto_resolved') {
            this.dialogRef.close({ resolved: true, acknowledged: true });
          }
        },
        error: () => {
          delete this.rowBusy[s.session_id];
          this.cdr.markForCheck();
        },
      });
  }

  /** Bulk-ack: alle aktuell sichtbaren Sessions auf einmal verwerfen. */
  dismissAll(): void {
    if (this.busy || this.sessions.length === 0) return;
    this.busy = true;
    this.cdr.markForCheck();
    this.analytics.resolveDmmViolation(this.data.notice.id, { action: 'dismiss' })
      .subscribe({
        next: () => this.dialogRef.close({ resolved: true, acknowledged: true }),
        error: () => {
          this.busy = false;
          this.cdr.markForCheck();
        },
      });
  }

  newDiagnostic(): void {
    if (this.busy) return;
    // Navigation only — keine DB-Aenderung. Banner bleibt offen, User muss
    // nach erneutem DMM-Compute die ggf. verbleibenden Sessions einzeln dismissen.
    this.dialogRef.close({ navigated: true, acknowledged: this.acknowledged });
    this.router.navigate(['/analyse/diagnostik/test/new']);
  }
}
